/**
 * PATH: packages/email-hub/src/feature/email-layout/SyncActivityPanel.tsx
 * FILE_ID: EMAIL-LAYOUT-SYNC-ACTIVITY-001
 * PURPOSE: Render EmailLayout's mailbox, active-sync, and backfill status.
 */

import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Loader2,
} from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import {
  formatCompact,
  formatRelativeTime,
  windowLabel,
} from './formatters'
import type { EmailLayoutController } from './useEmailLayoutController'

export function SyncActivityPanel({
  layout,
}: {
  layout: EmailLayoutController
}) {
  return (
    <>
      {layout.stats?.total != null && (
        <div className="rounded-md bg-muted/50 px-2 py-1.5 text-center">
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Mailbox
          </p>
          <p className="text-sm font-semibold tabular-nums text-foreground">
            {formatCompact(layout.stats.total)}
            <span className="ml-1 text-[10px] font-normal text-muted-foreground">
              emails
            </span>
          </p>
        </div>
      )}

      {layout.isSyncing && (
        <div className="space-y-1 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-2 py-1.5">
          <p className="flex items-center gap-1.5 text-[10px] font-medium text-emerald-700 dark:text-emerald-300">
            <Loader2 className="w-2.5 h-2.5 animate-spin" />
            Syncing inbox…
          </p>
          <div className="h-1 bg-emerald-500/10 rounded-full overflow-hidden">
            <div className="h-full w-1/3 bg-emerald-500 animate-pulse" />
          </div>
        </div>
      )}

      {!layout.isSyncing && layout.syncStatus?.active_sync && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-2 py-1.5">
          <p className="text-[10px] flex items-center justify-center gap-1.5 text-amber-700 dark:text-amber-300">
            <Loader2 className="w-2.5 h-2.5 animate-spin" />
            <span className="font-medium">
              Worker running{' '}
              {Math.round(layout.syncStatus.active_sync.age_seconds || 0)}s
            </span>
          </p>
          <p className="text-[10px] text-amber-700/70 dark:text-amber-300/70 text-center mt-0.5">
            Pulling emails from Microsoft - the count above updates as they land.
          </p>
        </div>
      )}
      {!layout.isSyncing
        && !layout.syncStatus?.active_sync
        && layout.syncStatus?.latest?.status === 'error' && (
        <div className="rounded-md border border-red-500/30 bg-red-500/5 px-2 py-1.5">
          <p className="text-[10px] flex items-center justify-center gap-1.5 text-red-700 dark:text-red-300">
            <AlertCircle className="w-2.5 h-2.5" />
            <span className="font-medium">Last sync errored</span>
          </p>
          <p
            className="text-[10px] text-red-700/70 dark:text-red-300/70 text-center mt-0.5 line-clamp-2"
            title={layout.syncStatus.latest.error_message || 'Unknown error'}
          >
            {layout.syncStatus.latest.error_message
              || 'Unknown error - see Recent issues.'}
          </p>
        </div>
      )}
      {!layout.isSyncing
        && !layout.syncStatus?.active_sync
        && layout.syncStatus?.latest
        && (
          layout.syncStatus.latest.status === 'success'
          || layout.syncStatus.latest.status === 'partial'
        ) && (
        <p className="text-[10px] text-center text-muted-foreground tabular-nums">
          Latest sync:&nbsp;
          <span className="font-medium text-foreground">
            +{layout.syncStatus.latest.emails_synced ?? 0} new
          </span>
          &nbsp;·&nbsp;{layout.syncStatus.latest.emails_found ?? 0} fetched
          {layout.syncStatus.latest.duration_seconds != null && (
            <>
              &nbsp;·&nbsp;
              {Math.round(layout.syncStatus.latest.duration_seconds)}s
            </>
          )}
        </p>
      )}
      {!layout.isSyncing
        && !layout.syncStatus?.latest
        && layout.lastSyncResult
        && !layout.lastSyncResult.queued
        && layout.lastSyncResult.success && (
        <p className="text-[10px] text-center text-muted-foreground tabular-nums">
          Latest sync:&nbsp;
          <span className="font-medium text-foreground">
            +{layout.lastSyncResult.emails_created ?? 0} new
          </span>
          &nbsp;·&nbsp;{layout.lastSyncResult.emails_fetched ?? 0} fetched
        </p>
      )}

      {!layout.backfill.state.complete && (
        <div className="space-y-1">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1">
              {layout.backfill.running && (
                <Loader2 className="w-2.5 h-2.5 animate-spin" />
              )}
              <span>Backfill</span>
              {layout.backfill.state.oldest_days > 0 && (
                <span className="opacity-70">
                  · last {windowLabel(layout.backfill.state.oldest_days)}
                </span>
              )}
            </span>
            <span className="font-mono tabular-nums">
              {layout.backfill.progressPct}%
            </span>
          </div>
          <div className="h-1 bg-border rounded-full overflow-hidden">
            <div
              className={cn(
                'h-full transition-all duration-500',
                layout.backfill.state.last_outcome === 'error'
                  ? 'bg-amber-500'
                  : 'bg-emerald-500',
              )}
              style={{ width: `${layout.backfill.progressPct}%` }}
            />
          </div>
          <p className="text-[10px] text-muted-foreground tabular-nums">
            <span className="text-foreground font-medium">
              +{formatCompact(layout.backfill.state.total_created)}
            </span>
            &nbsp;added&nbsp;·&nbsp;{layout.backfill.state.total_chunks}{' '}
            chunk{layout.backfill.state.total_chunks === 1 ? '' : 's'}
          </p>
          {layout.backfill.state.last_message && (
            <p
              className="text-[10px] text-muted-foreground/70 truncate"
              title={layout.backfill.state.last_message}
            >
              {layout.backfill.state.last_message}
            </p>
          )}
        </div>
      )}
      {layout.backfill.state.complete
        && layout.backfill.state.total_chunks > 0 && (
        <p className="text-[10px] text-emerald-600 dark:text-emerald-400 text-center flex items-center justify-center gap-1 tabular-nums">
          <CheckCircle2 className="w-2.5 h-2.5" />
          History caught up · +
          {formatCompact(layout.backfill.state.total_created)} added
        </p>
      )}

      {(() => {
        const summary = layout.syncStatus?.summary
        const oldestSeconds = summary?.oldest_sync_age_seconds ?? null
        if (oldestSeconds != null) {
          const oldestMs = Date.now() - oldestSeconds * 1000
          const isStale = oldestSeconds > 15 * 60
          const mailboxCount = summary?.active_mailbox_count ?? 0
          const errorFlag = summary?.any_with_error
          return (
            <p
              className={cn(
                'text-[10px] text-center flex items-center justify-center gap-1',
                isStale || errorFlag
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-muted-foreground',
              )}
              aria-live="polite"
              title={
                mailboxCount > 1
                  ? `Oldest of ${mailboxCount} active mailboxes`
                  : 'Server reports last successful sync for this mailbox'
              }
            >
              {(isStale || errorFlag) && (
                <AlertTriangle className="w-2.5 h-2.5" />
              )}
              {mailboxCount > 1 ? 'Oldest sync ' : 'Last synced '}
              {formatRelativeTime(oldestMs)}
              {errorFlag && <span> · sync error</span>}
            </p>
          )
        }
        return layout.lastSyncAt ? (
          <p
            className="text-[10px] text-muted-foreground text-center"
            aria-live="polite"
          >
            Last synced {formatRelativeTime(layout.lastSyncAt)}
          </p>
        ) : null
      })()}
    </>
  )
}
